import { Injectable, OnDestroy } from '@angular/core'
import { environment as env } from '../../../../../environments/environment'
import { AutocompleteOptions, Facet, Page, SearchResult, SmartSearch, SmartSearchOptions } from 'smartsearch.js'
import { BehaviorSubject, from, Observable, of, Subscription } from 'rxjs'
import { MbConstants } from '../../../shared/mb-constants'
import { CountryService } from '../../../site-context/facade/country.service'
import { URLParam } from 'smartsearch.js/dist/types'
import { WindowRef } from '@spartacus/core'
import { CmsResultData, ContentResultItem, JobResultItem } from '../../components/types'
import { map } from 'rxjs/operators'
import { MiVideo } from '../moving-image/moving-image'
import { ObjectUtils } from '../../../shared/utils/object-utils'
import { ParamMap } from '@angular/router'
import { ResultItemFlexType } from '../../enums/result-item-flex-type'
import { ContentType } from '../../enums/content-type'

@Injectable({
  providedIn: 'root'
})
export class SmartSearchService implements OnDestroy {

  showContentTab = new BehaviorSubject<boolean>(false)
  readonly showContentTab$ = this.showContentTab.asObservable()
  results: Page | undefined
  activeLanguage = MbConstants.FALLBACK_LANGUAGE.toLowerCase()
  subscription = new Subscription()
  private model = new BehaviorSubject<Page | undefined>(undefined)
  readonly model$ = this.model.asObservable()

  constructor (protected countryService: CountryService,
               protected winRef: WindowRef) {
    if (this.winRef.isBrowser()) {
      this.subscription.add(this.countryService.getActive().subscribe(country => {
        this.activeLanguage = country?.toLowerCase() ?? MbConstants.FALLBACK_COUNTRY.toLowerCase()
      }))
    }
  }

  async search (query: string, search?: string, ...customParams: URLParam[]): Promise<Page | undefined> {
    this.results = await this.preparedSearch(search)?.search(query, ...customParams)
    if (this.results) {
      this.results.facets = this.filterCategory(this.results.facets)
    }
    this.model.next(this.results)
    return this.results
  }

  preparedSearch (preparedSearchString?: string,
                  customParam?: URLParam[],
                  autocompleteOptions?: AutocompleteOptions) {
    const preparedSearch = preparedSearchString ?? env.smartSearchDefaultCaasGenerator
    const params: URLParam[] = [{ language: 'xx' }, { language: this.activeLanguage }] as URLParam[]
    const options: SmartSearchOptions = {}
    if (customParam?.length) {
      params.push(...customParam)
    }
    options.customParams = params
    if (autocompleteOptions) {
      options.autocompleteOptions = autocompleteOptions
    }
    // smartsearch currently does not support SSR
    return this.winRef.isBrowser() && new SmartSearch(env.smartSearchUrl, preparedSearch, options) || undefined
  }

  searchJobById (id: string): Observable<JobResultItem | undefined> {
    const search = this.preparedSearch(env.smartSearchJobs, [{ fq: `jobId:"${id}"` }])
    if (!search) {
      return of(undefined)
    }
    return from(search.search('*')).pipe(
      map(page => page?.searchResults?.[0] as any),
      map(result => result?.result),
      map(result => this.mapJobData(result))
    )
  }

  getAllVideos (): Observable<SearchResult[]> {
    const search = this.preparedSearch(env.smartSearchMiVideoSearch)
    if (!search) {
      return of([{} as SearchResult])
    }
    return from(search?.search('*')
      .then(res => res?.searchResults ?? [] as ContentResultItem[])
      .then(res => res.map((r: any) => r?.result))
    )
  }

  getAllChannels (): Observable<SearchResult[]> {
    const search = this.preparedSearch(env.smartSearchMiChannelSearch)
    if (!search) {
      return of([])
    }
    return from(search.search('*')
      .then(res => res?.searchResults ?? [])
      .then(res => res.map((r: any) => r?.result)))
  }

  async searchByField (query: string, field: string, param?: URLParam[], search?: string) {
    const fieldParam = { fq: `${field}:"${query}"` }
    param = [fieldParam, ...(param ?? [])]
    this.results = await this.preparedSearch(search, param)?.search('*')
    if (this.results) {
      this.results.facets = this.filterCategory(this.results.facets)
    }
    this.model.next(this.results)
    return this.results
  }

  autocomplete (query: string, preparedSearch?: string, field?: string): Promise<string[]> | undefined {
    if (!query?.length) {
      return new Promise(resolve => resolve([]))
    }
    const fieldParam = [{ field: field ?? 'FS_pt_pageTitle_keyword_lc' }]
    const autocompleteOptions = {
      prefixThreshold: 3,
      language: this.activeLanguage
    }
    return this.preparedSearch(preparedSearch, fieldParam, autocompleteOptions)?.fetchAutocompleteList(query)
  }

  setShowContentTab (show: boolean): void {
    this.showContentTab.next(show)
  }

  parseQueryParamsToURLParamsArray (params: ParamMap): URLParam[] {
    const query = params.get(MbConstants.QUERY_PARAM) || params.get(MbConstants.SEARCH_PARAM) || undefined
    const queryContent = params.get(MbConstants.QUERY_CONTENT_PARAM)
    const queryJob = params.get(MbConstants.QUERY_JOB_PARAM)
    const searchFilter = params.get(MbConstants.SEARCH_FILTER_PARAM)
    const param = `${queryContent ? `${queryContent}&` : ''}${queryJob ? `${queryJob}&` : ''}${searchFilter ? searchFilter : ''}`
    const urlParams: URLParam[] = []
    if (query) {
      urlParams.push({ query: query })
    }
    param.replace(/\+/g, ' ')
      .split('&')
      .forEach(p => {
        if (p?.length) {
          const paramSplit = p.split('=')
          const paramObject = Object.fromEntries([paramSplit])
          urlParams.push(paramObject)
        }
      })
    return urlParams
  }

  mapVideoData (data: CmsResultData | undefined): MiVideo {
    const description = data?.description?.[0] ?? ''
    return {
      id: data?.uid?.[0] ?? '',
      source: data,
      description: description === 'null' ? '' : description,
      title: data?.title?.[0] ?? '',
      length: +(data?.length?.[0] ?? 0),
      channels: (data?.channels as any ?? []).map((channel: string) => ObjectUtils.parseJSON(channel)),
      stills: (data?.stills ?? []).map((still: string) => ObjectUtils.parseJSON(still)),
      deleted: data?.deleted?.[0] === 'true' ?? false,
      published: data?.published?.[0] === 'true' ?? false,
      thumbnail: data?.thumbnail ?? '',
      downloadable: data?.downloadable?.[0] === 'true' ?? false
    }
  }

  mapJobData (data: CmsResultData | undefined): JobResultItem {
    return {
      flexType: ResultItemFlexType.RESULT_ITEM_JOB,
      contentType: ContentType.JOB,
      address: data?.address?.shift() ?? '',
      city: data?.cityName?.shift() ?? '',
      company: data?.company?.shift() ?? '',
      contactName: data?.contactName?.shift() ?? '',
      contactPhone: data?.contactPhone?.shift() ?? '',
      content: data?.content?.shift() ?? '',
      countryIso: data?.countryIsoCode?.shift() ?? '',
      countryName: data?.countryName?.shift() ?? '',
      division: data?.division?.shift() ?? '',
      employmentType: data?.employmentType?.shift() ?? '',
      experience: data?.experienceLevel?.shift() ?? '',
      externalLink: data?.externalLink?.shift() ?? '',
      id: data?.id ?? '',
      jobId: data?.jobId?.shift() ?? '',
      jobField: data?.jobField?.shift() ?? '',
      link: data?.link ?? '',
      provider: data?.provider?.shift() ?? '',
      startDate: data?.startDate?.shift() ?? '',
      title: data?.title?.shift() ?? '',
      videoId: data?.videoId?.shift() ?? '',
      workTime: data?.workTime?.shift() ?? ''
    }
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  private filterCategory (facets: Facet[] = []): Facet[] {
    return facets.filter(facet => facet?.config?.baseName !== MbConstants.CATEGORY_FACET_CODE)
  }
}
